/**
 * KPIs "de escaparate" de las tarjetas flotantes del hero (Hero.tsx).
 *
 * NO son datos reales: hoy no hay una fuente rápida para las ventas del día ni
 * las recargas de tarjeta, así que se INVENTAN de forma determinista y coherente
 * con la hora del día (por la mañana poco, luego crece) hasta poder sacarlos de
 * la realidad. Función pura: mismo `now` → mismo resultado (nada de aleatoriedad
 * viva), así el número no "parpadea" entre renders del mismo minuto y es testeable.
 *
 * Rangos pedidos (Víctor): ventas del día en [10 000, 100 000 000] y monto de la
 * recarga en [3, 120]. El "tamaño" del negocio se ancla por MES (estable dentro
 * del mes) con una deriva diaria pequeña, para que las ventas no salten de forma
 * absurda de un día a otro pero sí comparen contra ayer.
 */

export const SALES_MIN = 10_000
export const SALES_MAX = 100_000_000
export const RECHARGE_MIN = 3
export const RECHARGE_MAX = 120

/** Ancla mensual del volumen de fin de día (log-uniforme dentro de la banda). */
const ANCHOR_MIN = 18_000
const ANCHOR_MAX = 70_000_000

/** La actividad de ventas arranca ~5:00; antes hay un piso mínimo. */
const ACTIVITY_START = 5 / 24
const MORNING_FLOOR = 0.04

export interface HeroStats {
  /** Ventas acumuladas "hoy" (entero, en [10K, 100M]). */
  salesToday: number
  /** Variación porcentual frente a "ayer" (entero, con signo). */
  vsYesterdayPct: number
  recharge: {
    /** Monto de la última recarga, en [3, 120] con 2 decimales. */
    amount: number
    /** "hace N min" (1..9). */
    minutesAgo: number
  }
  /** Progreso del día [0..1] — alimenta la barra de la tarjeta. */
  dayProgress: number
}

/** Hash de cadena a entero de 32 bits (xmur3). */
function hashSeed(str: string): number {
  let h = 1779033703 ^ str.length
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353)
    h = (h << 13) | (h >>> 19)
  }
  h = Math.imul(h ^ (h >>> 16), 2246822507)
  h = Math.imul(h ^ (h >>> 13), 3266489909)
  h ^= h >>> 16
  return h >>> 0
}

/** PRNG determinista mulberry32 a partir de una semilla entera. */
function seededRandom(seed: number): number {
  let a = seed
  a |= 0
  a = (a + 0x6d2b79f5) | 0
  let t = Math.imul(a ^ (a >>> 15), 1 | a)
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296
}

/** Número pseudoaleatorio estable en [0,1) a partir de una clave de texto. */
function randFromKey(key: string): number {
  return seededRandom(hashSeed(key))
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

const dayKey = (d: Date): string =>
  `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`

const monthKey = (d: Date): string => `${d.getFullYear()}-${d.getMonth() + 1}`

/** Volumen objetivo de FIN de día para una fecha dada (determinista). */
function dailyTarget(day: Date): number {
  // Ancla del mes en escala logarítmica (cubre toda la banda sin pinchar los topes).
  const anchor = Math.exp(
    lerp(Math.log(ANCHOR_MIN), Math.log(ANCHOR_MAX), randFromKey(monthKey(day))),
  )
  // Deriva diaria sesgada a positivo (-15% .. +30%): la comparación con ayer
  // tiende a crecer, como pidió el owner, sin ser monótona artificial.
  const drift = lerp(-0.15, 0.3, randFromKey(dayKey(day)))
  return anchor * (1 + drift)
}

/** Fracción del día realizada [0..1], monótona y con arranque suave (~5:00). */
function dayCurve(now: Date): number {
  const secondsIntoDay =
    now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()
  const fraction = secondsIntoDay / 86_400
  const p = clamp((fraction - ACTIVITY_START) / (1 - ACTIVITY_START), 0, 1)
  const smooth = p * p * (3 - 2 * p) // smoothstep (monótona en [0,1])
  return MORNING_FLOOR + (1 - MORNING_FLOOR) * smooth
}

/**
 * Calcula los KPIs del hero para un instante dado. PURO y determinista.
 */
export function computeHeroStats(now: Date): HeroStats {
  const curve = dayCurve(now)

  const targetToday = dailyTarget(now)
  const yesterday = new Date(now.getTime() - 86_400_000)
  const targetYesterday = dailyTarget(yesterday)

  const salesToday = Math.round(clamp(targetToday * curve, SALES_MIN, SALES_MAX))
  const vsYesterdayPct = Math.round((targetToday / targetYesterday - 1) * 100)

  // Recarga: tope diario en [20,120] y crecimiento suave durante el día.
  const rechargeCap = lerp(20, RECHARGE_MAX, randFromKey(`${dayKey(now)}:recharge`))
  const rechargeAmount = clamp(
    rechargeCap * (0.25 + 0.75 * curve),
    RECHARGE_MIN,
    RECHARGE_MAX,
  )

  return {
    salesToday,
    vsYesterdayPct,
    recharge: {
      amount: Math.round(rechargeAmount * 100) / 100,
      minutesAgo: 1 + (now.getMinutes() % 9),
    },
    dayProgress: curve,
  }
}
